import { FC, memo, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiContentDrawer,
  MiDrawerHeader,
  MiLoader,
  MiTab,
} from "@miplatform/components";
import {
  APP_BAR_HEIGHT,
  APP_BAR_HEIGHT_TABLET,
  ATTEMPT_STATUSES,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  HTTP_STATUSES,
  MIBUILD_PAGE_HEADER_HEIGHT,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { IAttempt, LoaderType } from "@miplatform/types";
import { mdiClose, mdiHomeOutline } from "@mdi/js";
import { useWorkOrder } from "@mibuildweb/hooks";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { WorkOrderDrawerTabs } from "./components/WorkOrderAttemptDrawer.types";
import { WorkOrderDrawerSubheader } from "./components/WorkOrderDrawerSubHeader";
import { WorkOrderDrawerDetailsTab } from "./components/tabs/WorkOrderDrawerDetailsTab";
import { WorkOrderDrawerDocumentsTab } from "./components/tabs/WorkOrderDrawerDocumentsTab";
import { WorkOrderDrawerHistoryTab } from "./components/tabs/WorkOrderDrawerHistoryTab";
import { WorkOrderDrawerStepsTab } from "./components/tabs/WorkOrderDrawerStepsTab";
import { useCalendarContext } from "../../../providers";
import { HoldAttemptFormData } from "../NhcCalendar.types";
import { WorkOrderAttemptDrawerActivity } from "../WorkOrderAttemptActivity/WorkOrderAttemptDrawerActivity";
import { WorkOrderHoldModal } from "../WorkOrderHoldModal/WorkOrderHoldModal";
import { useWorkOrderAttemptDrawerViewModel } from "./useWorkOrderAttemptDrawerViewModel";

export const WorkOrderAttemptDrawer: FC<WorkOrderAttemptDrawerProps> = memo(
  ({ open = false, listAttempt, onAttemptUpdated, onClose }) => {
    const theme = useTheme();
    const { classes } = useStyles();
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const { address } = listAttempt?.home ?? {};

    const [activeTab, setActiveTab] = useState<WorkOrderDrawerTabs>(
      WorkOrderDrawerTabs.Steps
    );
    const { isCalendarLoading, calendarSet, holdWorkOrder, refreshData } =
      useCalendarContext();
    const handleClose = () => onClose(listAttempt.id as string);

    const {
      attempt: fetchedAttempt,
      steps,
      isLoading,
      stateManager,
      getAttempt,
      handleAttemptOpen,
      // TODO: Re-implement with Work Order execution
      // handleAttemptClose,
      // handleAttemptPending,
    } = useWorkOrderAttemptDrawerViewModel({
      listAttempt,
      onClose: handleClose,
      onAttemptUpdated,
    });
    const [isHoldModalOpen, handleHoldModalOpen, handleHoldModalClose] =
      useBoolean();

    const {
      workOrder,
      getWorkOrder,
      isLoading: isWorkOrderLoading,
    } = useWorkOrder(listAttempt.assignment?.workOrder?.workOrderId ?? null);

    const tabs = [
      {
        label: "Steps",
        value: WorkOrderDrawerTabs.Steps,
      },
      {
        label: "Documents",
        value: WorkOrderDrawerTabs.Documents,
      },
      {
        label: "Details",
        value: WorkOrderDrawerTabs.Details,
      },
      {
        label: "History",
        value: WorkOrderDrawerTabs.History,
      },
      {
        label: "Activity",
        value: WorkOrderDrawerTabs.Activity,
      },
    ];

    const DRAWER_TOP_OFFSET =
      (isTablet ? APP_BAR_HEIGHT_TABLET : APP_BAR_HEIGHT) +
      MIBUILD_PAGE_HEADER_HEIGHT;

    const attempt = useMemo(
      () => ({ ...listAttempt, ...fetchedAttempt }),
      [fetchedAttempt, listAttempt]
    );
    const canHold = useMemo(
      () =>
        !(
          attempt.attemptStatus === ATTEMPT_STATUSES.HOLD ||
          attempt.attemptStatus === ATTEMPT_STATUSES.CANCELLED ||
          attempt.attemptStatus === ATTEMPT_STATUSES.CLOSED
        ),
      [attempt]
    );

    // TODO: Re-implement with Work Order execution
    // const handleDrawerAndAttemptClose = () => {
    //   handleClose();
    //   handleAttemptClose();
    // };

    const submitHold = async (data: HoldAttemptFormData) => {
      const response = await holdWorkOrder(data);

      if (response.status === HTTP_STATUSES.OK) {
        handleHoldModalClose();
        refreshData();
        getAttempt();
      }
    };

    useEffect(() => {
      if (open) {
        getWorkOrder();
        handleAttemptOpen();
      }
    }, [open]);

    return (
      <MiContentDrawer
        variant="persistent"
        isLoading={isLoading || isWorkOrderLoading}
        open={open}
        onClose={handleClose}
        hideBackdrop
        sx={{
          "& > .MuiPaper-root": {
            top: DRAWER_TOP_OFFSET,
            height: `calc(100% - ${DRAWER_TOP_OFFSET}px)`,
            zIndex: theme.zIndex.drawer,
          },
        }}
        actions={[
          {
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            name: `Cancel`,
            onClick: handleClose,
          },
          canHold && {
            variant: BUTTON_VARIANTS.OUTLINED,
            name: "Hold Work Order",
            color: BUTTON_COLORS.PURPLE,
            onClick: handleHoldModalOpen,
          },
          {
            variant: BUTTON_VARIANTS.CONTAINED,
            title: `Complete ${attempt.assignment?.assignmentTemplateName}`,
          },
        ].filter(Boolean)}
      >
        <Box className={classes.content}>
          <MiDrawerHeader
            title={listAttempt?.home?.address.addressLine1 || ""}
            subtitle={[
              address ? `${address.city}, ${address.regionCode}` : "",
              listAttempt?.home?.communityName ?? "",
              listAttempt?.home?.builderName ?? "",
            ]}
            leftIcon={mdiHomeOutline}
            rightAction={
              <MiActions
                type="ICONS"
                actions={[
                  {
                    name: "Close",
                    icon: mdiClose,
                    onClick: handleClose,
                  },
                ]}
              />
            }
          />
          <MiLoader isLoading={isLoading} type={LoaderType.Linear} />
          <Typography className={classes.title}>
            {attempt.assignment?.assignmentTemplateName}
          </Typography>
          <Box className={classes.subheaderContainer}>
            <WorkOrderDrawerSubheader workOrder={attempt} />
          </Box>
          <MiTab
            tabs={tabs}
            onTabChange={setActiveTab}
            currentTab={activeTab}
            borderBottom
          />
          <Box className={classes.tabContent}>
            {activeTab === WorkOrderDrawerTabs.Steps && (
              <WorkOrderDrawerStepsTab
                attempt={attempt}
                steps={steps}
                handleClose={handleClose}
                getAttempt={getAttempt}
              />
            )}
            {activeTab === WorkOrderDrawerTabs.Documents && workOrder && (
              <WorkOrderDrawerDocumentsTab workOrder={workOrder} />
            )}
            {activeTab === WorkOrderDrawerTabs.History && (
              <WorkOrderDrawerHistoryTab />
            )}
            {activeTab === WorkOrderDrawerTabs.Activity && (
              <WorkOrderAttemptDrawerActivity
                attempt={attempt}
                stateManager={stateManager}
              />
            )}
            {activeTab === WorkOrderDrawerTabs.Details && (
              <WorkOrderDrawerDetailsTab
                home={attempt.home || null}
                attempt={attempt}
              />
            )}
          </Box>
        </Box>
        <WorkOrderHoldModal
          open={isHoldModalOpen}
          onClose={handleHoldModalClose}
          onHold={(data) => submitHold({ ...data, attempt })}
          calendarSet={calendarSet?.config ?? null}
          isLoading={!!isCalendarLoading || isLoading}
        />
      </MiContentDrawer>
    );
  }
);

interface WorkOrderAttemptDrawerProps {
  open?: boolean;
  listAttempt: IAttempt;
  onClose: (attemptId: string) => void;
  onAttemptUpdated?: (attempt: IAttempt) => void;
}

const useStyles = makeStyles()((theme) => ({
  drawer: {
    borderLeft: `${theme.spacing(2)} solid ${theme.palette.grey.accent}`,
    boxSizing: "content-box",
    overflow: "auto",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  title: {
    fontWeight: theme.fontWeight.semiBold,
    padding: theme.spacing(0, 4, 0, 4),
  },
  subheaderContainer: {
    padding: theme.spacing(4),
  },
  tabContent: {
    padding: theme.spacing(4),
  },
}));
